import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { adminApi, getToken } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, ArrowLeft, Loader2, CalendarX2 } from "lucide-react";

function todayStr() {
  return format(new Date(), "yyyy-MM-dd");
}

export default function PrintSchedule() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get("date") || todayStr();
  const autoprint = searchParams.get("autoprint") === "1";
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printed, setPrinted] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin");
    }
  }, [navigate]);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/appointments", { params: { date, status: "booked" } });
      setAppointments(res.data.appointments);
    } catch (e) { /* interceptor handles 401 */ } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Auto-print once after data loads when opened via the dashboard Print button
  useEffect(() => {
    if (autoprint && !loading && !printed) {
      const t = setTimeout(() => {
        setPrinted(true);
        window.print();
      }, 600);
      return () => clearTimeout(t);
    }
  }, [autoprint, loading, printed]);

  const dateDisplay = (() => {
    try {
      return format(new Date(`${date}T12:00:00`), "EEEE, MMMM d, yyyy");
    } catch {
      return date;
    }
  })();

  const changeDate = (e) => {
    const v = e.target.value;
    if (v) setSearchParams({ date: v });
  };

  return (
    <div className="min-h-screen bg-white text-black print-schedule-page">
      {/* Toolbar - hidden when printing */}
      <div className="print-hide sticky top-0 bg-[color:var(--brand-navy)] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <Link
            to="/admin/dashboard"
            className="text-xs text-white/70 hover:text-white inline-flex items-center gap-1.5 transition-colors duration-200"
            data-testid="print-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
          </Link>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={date}
              onChange={changeDate}
              className="h-9 w-auto bg-white text-black"
              data-testid="print-date-input"
            />
            <Button
              size="sm"
              className="bg-[color:var(--brand-sky)] hover:bg-[#1791c5] text-white btn-lift"
              onClick={() => window.print()}
              data-testid="print-now-button"
            >
              <Printer className="h-4 w-4 mr-1.5" /> Print
            </Button>
          </div>
        </div>
      </div>

      {/* Printable sheet */}
      <div className="max-w-4xl mx-auto px-6 py-8 print-sheet">
        <header className="flex items-start justify-between gap-4 border-b-2 border-black pb-4">
          <div>
            <img src="/logo.png" alt="Cayman Fingerprinting Services Ltd." style={{ height: 52, width: "auto" }} />
            <p className="text-[11px] mt-2 text-gray-700">
              93 Hospital Rd., SIGMA Building, 1st Floor, George Town &nbsp;&middot;&nbsp; 345-928-0382 &nbsp;&middot;&nbsp; info@caymanfingerprinting.com
            </p>
          </div>
          <div className="text-right">
            <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>Daily Schedule</h1>
            <p className="text-sm font-semibold mt-1" data-testid="print-date-display">{dateDisplay}</p>
            <p className="text-[10px] text-gray-500 mt-1">Printed {format(new Date(), "MMM d, yyyy h:mm a")}</p>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-500 print-hide">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading schedule...
          </div>
        ) : appointments.length === 0 ? (
          <div className="py-16 text-center" data-testid="print-empty-state">
            <CalendarX2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="font-semibold text-gray-700">No appointments scheduled for this day</p>
          </div>
        ) : (
          <>
            <table className="w-full mt-5 border-collapse print-table" data-testid="print-schedule-table">
              <thead>
                <tr>
                  <th className="text-left text-[11px] uppercase tracking-wide border-b-2 border-black py-2 pr-2 w-[70px]">Time</th>
                  <th className="text-left text-[11px] uppercase tracking-wide border-b-2 border-black py-2 pr-2">Customer</th>
                  <th className="text-left text-[11px] uppercase tracking-wide border-b-2 border-black py-2 pr-2">Service</th>
                  <th className="text-left text-[11px] uppercase tracking-wide border-b-2 border-black py-2 pr-2 w-[110px]">Phone</th>
                  <th className="text-left text-[11px] uppercase tracking-wide border-b-2 border-black py-2 pr-2 w-[90px]">Conf. #</th>
                  <th className="text-left text-[11px] uppercase tracking-wide border-b-2 border-black py-2 pr-2">Notes</th>
                  <th className="text-center text-[11px] uppercase tracking-wide border-b-2 border-black py-2 w-[64px]">Arrived</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((a) => (
                  <tr key={a.id} className="align-top" data-testid="print-schedule-row">
                    <td className="py-2.5 pr-2 border-b border-gray-300 font-bold text-sm whitespace-nowrap">{a.time_display}</td>
                    <td className="py-2.5 pr-2 border-b border-gray-300">
                      <span className="font-semibold text-sm">{a.name}</span>
                      <span className="block text-[11px] text-gray-600">{a.email}</span>
                    </td>
                    <td className="py-2.5 pr-2 border-b border-gray-300 text-[12px]">{a.service}</td>
                    <td className="py-2.5 pr-2 border-b border-gray-300 text-[12px] whitespace-nowrap">{a.phone}</td>
                    <td className="py-2.5 pr-2 border-b border-gray-300 text-[12px] font-mono">{a.confirmation_number}</td>
                    <td className="py-2.5 pr-2 border-b border-gray-300 text-[11px] text-gray-700">{a.notes || ""}</td>
                    <td className="py-2.5 border-b border-gray-300 text-center">
                      <span className="inline-block h-4.5 w-4.5 border-2 border-black rounded-sm" style={{ height: 18, width: 18 }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex items-center justify-between mt-5 pt-3 border-t border-gray-400">
              <p className="text-sm font-semibold" data-testid="print-total-count">
                Total appointments: {appointments.length}
              </p>
              <p className="text-[10px] text-gray-500">Business hours: Monday &ndash; Friday, 9:00 AM &ndash; 3:00 PM</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
